import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowDown } from '../../Icons/Icon';
import { setCurrentLanguage, getCurrentLanguage, subscribeToLanguageChange } from '../../../utils/languageStore';

const langConfig = [
  { code: 'en', labelKey: 'english', dir: 'ltr' },
  { code: 'ar', labelKey: 'arabic', dir: 'rtl' },
  { code: 'ckb', labelKey: 'kurdish', dir: 'rtl' },
];

const dropdownVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: { 
    opacity: 1,
    y: 0,
    transition: { duration: 0.2 }
  },
  exit: { opacity: 0, y: -10 }
};

export default function SwitchLang() {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLangState] = useState(getCurrentLanguage());
  const containerRef = useRef(null);

  useEffect(() => {
    const unsubscribe = subscribeToLanguageChange(newLang => {
      setCurrentLangState(newLang);
    });
    return () => unsubscribe();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [containerRef]);

  const changeLanguage = lang => {
    const selected = langConfig.find(l => l.code === lang);
    if (!selected) return;

    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
    localStorage.setItem('language', lang);
    document.documentElement.setAttribute('dir', selected.dir);
    setCurrentLangState(lang);
    setIsOpen(false);
  };

  const current = langConfig.find(l => l.code === currentLang) || langConfig[0];

  return (
    <div ref={containerRef} className="relative lang-text inline-block text-left" dir={current.dir}>
      <motion.button
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => setIsOpen(o => !o)}
        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white hover:shadow-sm transition-all"
      >
        <span className="font-medium text-gray-700">{t(current.labelKey)}</span>
        <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ArrowDown className="text-gray-600" />
        </motion.span>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={`absolute mt-2 w-48 ${['ar','ckb'].includes(i18n.language) ? 'left-1' : 'right-1'} rounded-lg bg-white border border-gray-200 shadow-lg z-50`}
          >
            <ul className="p-2 space-y-1">
              {langConfig.map(({ code, labelKey }) => (
                <li key={code}>
                  <button
                    onClick={() => changeLanguage(code)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                      code === currentLang ? 'bg-teal-50 text-teal-600' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span>{t(labelKey)}</span>
                    {code === currentLang && <div className="ml-auto w-2 h-2 bg-teal-500 rounded-full" />}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
